import { BigNumber } from '@ethersproject/bignumber'
import { useMemo } from 'react'
import {
  usePerpetualRequirementMargin,
  useTransferRequirementMargin,
} from './useRequirementMargin'
import { useTotalFrozenMargin } from './useTotalFrozenMargin'
import { useTotalSpotConversionMarginValue } from './useTotalSpotConversionMarginValue'

/**
 * Calculates the total available margin value for trading perpetual contract.
 *
 * @returns The total available margin value as a BigNumber.
 */
export function useTotalPerpetualAvailableMarginValue(): BigNumber {
  const conversionMargin = useTotalSpotConversionMarginValue()
  const requirementMargin = usePerpetualRequirementMargin()
  const frozenMargin = useTotalFrozenMargin()
  return useMemo(() => {
    return conversionMargin.sub(requirementMargin).sub(frozenMargin)
  }, [conversionMargin, requirementMargin, frozenMargin])
}

/**
 * Calculates the total transferable available margin value.
 * Used for transfer/withdrawal/spot-trading.
 * It subtracts the requirement margin and frozen margin from the total spot conversion margin.
 *
 * @returns The total transferable available margin value as a BigNumber.
 */
export function useTotalTransferableAvailableMarginValue(): BigNumber {
  const conversionMargin = useTotalSpotConversionMarginValue()
  const requirementMargin = useTransferRequirementMargin()
  const frozenMargin = useTotalFrozenMargin()
  return useMemo(() => {
    const requirementBuffer = requirementMargin.gt('0')
      ? requirementMargin.mul(105).div(100)
      : requirementMargin

    return conversionMargin.sub(requirementBuffer).sub(frozenMargin)
  }, [conversionMargin, requirementMargin, frozenMargin])
}

/**
 * Calculates the total available margin value for liquidation.
 *
 * @returns The total available margin value as a BigNumber.
 */
export function useTotalLiquidationAvailableMarginValue(): BigNumber {
  const conversionMargin = useTotalSpotConversionMarginValue()
  const frozenMargin = useTotalFrozenMargin()
  return useMemo(() => {
    return conversionMargin.sub(frozenMargin)
  }, [conversionMargin, frozenMargin])
}
